import { type ServiceParams, unwrapJson } from "../client";
import type {
  AuditLogFilters,
  AuditLogPage,
  CostResponse,
  LatencyByStageResponse,
  ObservabilityWindow,
  PolicyEnforcementResponse,
  PostureSummaryResponse,
  TopModelsResponse,
  UsageCounters,
  UsdIdrRateGetResponse,
  UsdIdrRateResponse,
  UsdIdrRateUpdatePayload,
} from "./types";

/**
 * Observability service — wraps the endpoints under
 * ``/v1/governance/observability/*`` plus the audit log
 * ``/v1/audit/logs``. The Sesi FE-2 surface specifically uses:
 *
 * - Overview tab: posture summary + latency + top models + audit
 * - Inference tab: latency by stage + top models
 * - Policy tab:    policy-enforcement summary
 * - Audit tab:     paginated /v1/audit/logs with filters
 *
 * The Throughput sub-table from the spec was dropped because the
 * backend does not expose a per-bucket throughput endpoint.
 */

interface WindowedQuery {
  window?: ObservabilityWindow;
  workspace_id?: string;
}

const Observability = ({ url, config }: ServiceParams) => ({
  async getPostureSummary(
    workspace_id?: string
  ): Promise<PostureSummaryResponse> {
    const qs = workspace_id
      ? `?workspace_id=${encodeURIComponent(workspace_id)}`
      : "";
    const response = await fetch(
      `${url}/v1/governance/observability/posture/summary${qs}`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<PostureSummaryResponse>(response);
  },

  async getInferenceLatency(
    params: WindowedQuery = {}
  ): Promise<LatencyByStageResponse> {
    const qs = new URLSearchParams();
    qs.set("window", params.window ?? "24h");
    if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
    const response = await fetch(
      `${url}/v1/governance/observability/inference/latency?${qs}`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<LatencyByStageResponse>(response);
  },

  async getTopModels(
    params: WindowedQuery & { limit?: number } = {}
  ): Promise<TopModelsResponse> {
    const qs = new URLSearchParams();
    qs.set("window", params.window ?? "24h");
    if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
    if (params.limit) qs.set("limit", String(params.limit));
    const response = await fetch(
      `${url}/v1/governance/observability/inference/top-models?${qs}`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<TopModelsResponse>(response);
  },

  async getCost(params: WindowedQuery = {}): Promise<CostResponse> {
    const qs = new URLSearchParams();
    qs.set("window", params.window ?? "24h");
    if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
    const response = await fetch(
      `${url}/v1/governance/observability/inference/cost?${qs}`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<CostResponse>(response);
  },

  async getUsage(
    params: { period?: string; workspace_id?: string } = {}
  ): Promise<UsageCounters> {
    const qs = new URLSearchParams();
    qs.set("period", params.period ?? "month");
    if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
    const response = await fetch(
      `${url}/v1/governance/observability/usage?${qs}`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<UsageCounters>(response);
  },

  async getFxRate(): Promise<UsdIdrRateGetResponse> {
    const response = await fetch(
      `${url}/v1/governance/observability/pricing/usd-idr-rate`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<UsdIdrRateGetResponse>(response);
  },

  async updateFxRate(
    payload: UsdIdrRateUpdatePayload
  ): Promise<UsdIdrRateResponse> {
    const response = await fetch(
      `${url}/v1/governance/observability/pricing/usd-idr-rate`,
      {
        method: "PUT",
        headers: config(),
        body: JSON.stringify(payload),
      }
    );
    return unwrapJson<UsdIdrRateResponse>(response);
  },

  async getPolicyEnforcement(
    params: WindowedQuery = {}
  ): Promise<PolicyEnforcementResponse> {
    const qs = new URLSearchParams();
    qs.set("window", params.window ?? "7d");
    if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
    const response = await fetch(
      `${url}/v1/governance/observability/policy-enforcement?${qs}`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<PolicyEnforcementResponse>(response);
  },

  async getAuditLogs(filters: AuditLogFilters = {}): Promise<AuditLogPage> {
    const qs = new URLSearchParams();
    qs.set("limit", String(filters.limit ?? 20));
    qs.set("offset", String(filters.offset ?? 0));
    if (filters.action) qs.set("action", filters.action);
    if (filters.log_status) qs.set("log_status", filters.log_status);
    if (filters.start_date) qs.set("start_date", filters.start_date);
    if (filters.end_date) qs.set("end_date", filters.end_date);
    if (filters.pii_detected !== undefined) {
      qs.set("pii_detected", String(filters.pii_detected));
    }
    const response = await fetch(`${url}/v1/audit/logs?${qs}`, {
      method: "GET",
      headers: config(),
    });
    return unwrapJson<AuditLogPage>(response);
  },
});

export default Observability;
